import type { IHeaderParams } from 'ag-grid-community';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDynamicCallback } from '../../hooks';
import { safeGridApi } from '../BlotterTable/utils';
import { HStack } from '../Core';
import { ICON_SIZES, Icon, IconName } from '../Icons';

interface AgGridGroupToggleHeaderProps extends IHeaderParams {
  columnsInGroup: string[];
}

export const AgGridGroupToggleHeader = ({
  progressSort,
  column,
  api,
  displayName,
  columnsInGroup = [],
}: AgGridGroupToggleHeaderProps) => {
  const [allColumnsInGroupCollapsed, setAllColumnsInGroupCollapsed] = useState(false);

  const updateColumnCollapsed = useDynamicCallback(() => {
    const safeApi = safeGridApi(api);
    const allColumns = safeApi?.getAllGridColumns();
    if (!allColumns) {
      // Both the api itself can be in the process of being destroyed,
      // and the column model can be in a bad state, leading to getAllGridColumns _sometimes_ returning undefined.
      // Guard against both here. Bit arbitrary...
      return;
    }

    const isAllCollapsed = columnsInGroup.every(id => {
      const column = allColumns.find(c => c.getColId() === id);
      if (column) {
        return !column.isVisible();
      }
      return true;
    });
    setAllColumnsInGroupCollapsed(isAllCollapsed);
  });

  useEffect(() => {
    updateColumnCollapsed();
  }, [updateColumnCollapsed]);

  const onSortRequested: React.MouseEventHandler<HTMLDivElement> = useCallback(
    e => {
      progressSort(e.shiftKey);
    },
    [progressSort]
  );
  const [currentSort, setCurrentSort] = useState(() => column.getSort());

  const onHandleColumnVisibilityChange = useDynamicCallback(() => {
    updateColumnCollapsed();
  });

  const handleToggle = useDynamicCallback((ev: React.MouseEvent) => {
    const safeApi = safeGridApi(api);
    if (!safeApi) {
      return;
    }

    ev.preventDefault();
    ev.stopPropagation();
    safeApi.setColumnsVisible(columnsInGroup, allColumnsInGroupCollapsed);
  });

  useEffect(() => {
    safeGridApi(api)?.addEventListener('columnVisible', onHandleColumnVisibilityChange);
    return () => {
      safeGridApi(api)?.removeEventListener('columnVisible', onHandleColumnVisibilityChange);
    };
  }, [api, onHandleColumnVisibilityChange]);

  useEffect(() => {
    const columnSortChanged = () => {
      setCurrentSort(column.getSort());
    };
    safeGridApi(api)?.addEventListener('sortChanged', columnSortChanged);
    return () => {
      safeGridApi(api)?.removeEventListener('sortChanged', columnSortChanged);
    };
  }, [api, column]);

  return (
    <HStack h="100%" alignItems="center" w="100%" justifyContent="flex-start" onClick={onSortRequested} gap="spacing2">
      {displayName}
      <HStack px="spacing12" py="spacing4" onClick={handleToggle}>
        <Icon
          icon={allColumnsInGroupCollapsed ? IconName.Columns : IconName.ExpandLeft}
          size={ICON_SIZES.SMALL}
          data-testid="group-toggle-header-button"
        />
      </HStack>
      {currentSort && (
        <Icon icon={currentSort === 'desc' ? IconName.ArrowDown : IconName.ArrowUp} size={ICON_SIZES.SMALL} />
      )}
    </HStack>
  );
};
